// Single source of truth for festival facts and the stage lineup.
// Set times follow the official 2026 "Full Lineup" flyer, stages in its order.

export const FESTIVAL = {
  name: "8th Annual Culver City Arts Festival",
  dateLabel: "Saturday, October 10, 2026",
  date: "2026-10-10",
  hoursLabel: "11am – 6pm",
  // Festival hours, Pacific time.
  startsAt: "2026-10-10T11:00:00-07:00",
  endsAt: "2026-10-10T18:00:00-07:00",
  timeZone: "America/Los_Angeles",
  street: "Washington Blvd",
  district: "Culver City Arts District",
  city: "Culver City, CA",
  hashtag: "#CulverCityArtsFestival",
  siteUrl: "https://culvercityartsfestival.com",
  donateUrl: "https://givebutter.com/8MgJGG",
  vendorUrl: "https://pci.jotform.com/form/261509482530153",
  volunteerUrl:
    "https://docs.google.com/forms/d/e/1FAIpQLSfF4rpkRAINbkPb_EWW16ceQHP-4CrBNLvM5X0kMZ51yagx3Q/viewform",
};

export const STAGES = [
  {
    id: "hutchinson",
    name: "Hutchinson Stage",
    color: "#0C7FBC",
    sets: [
      { start: "11:00", end: "12:00", act: "Cheek to Cheek" },
      { start: "12:00", end: "13:00", act: "Fred Sokolow Jazz Duo" },
      { start: "13:00", end: "14:00", act: "Urth Rhythms" },
      { start: "14:00", end: "15:00", act: "Josh Levy" },
      { start: "15:00", end: "16:00", act: "Erin & Christian Tomita" },
      { start: "16:00", end: "18:00", act: "Fleece Kawasaki" },
    ],
  },
  {
    id: "sherbourne",
    name: "Sherbourne Stage",
    color: "#D6336C",
    sets: [
      { start: "11:00", end: "12:00", act: "DJ Set" },
      { start: "12:00", end: "12:50", act: "Yoga Six Culver City" },
      { start: "13:00", end: "13:45", act: "Rangalee Dance Company" },
      { start: "14:00", end: "14:45", act: "School of Rock" },
      { start: "15:00", end: "15:50", act: "Free Salsa Lesson" },
      { start: "16:00", end: "18:00", act: "Los Calicubanos" },
    ],
  },
  {
    id: "mcmanus",
    name: "McManus Stage",
    color: "#C2410C",
    sets: [
      { start: "11:00", end: "12:00", act: "Main Street Jam" },
      { start: "12:00", end: "13:00", act: "Double Batch Daddy" },
      { start: "13:00", end: "14:00", act: "The Front Yard" },
      { start: "14:00", end: "15:00", act: "The Mighty Vornados" },
      { start: "15:00", end: "16:00", act: "Top Shelf Brass Band" },
      { start: "16:00", end: "18:00", act: "Upstream Music" },
    ],
  },
];

// Roaming dance performance, seen across the festival grounds.
export const SPECIAL_PERFORMANCE = {
  id: "heidi-duckler-dance",
  name: 'Heidi Duckler Dance "Ordena"',
  shortName: "Heidi Duckler Dance",
  description: "Roaming performance: catch it across the festival grounds.",
  color: "#7E3FA0",
  sets: [
    { start: "11:45", end: "12:00" },
    { start: "12:45", end: "13:00" },
    { start: "13:45", end: "14:00" },
    { start: "14:45", end: "15:00" },
  ],
};

export const STREET_MURALISTS = {
  description: "Live mural painting throughout the festival grounds.",
  color: "#0F7B7B",
  artists: ["Mayra Diaz", "Daisuke Okmoto", "Nicole Sherman", "Jen Lavita", "Gus Harper"],
};

export const slugify = (text) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

// "13:15" -> "1:15pm"
export const formatClock = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "pm" : "am";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")}${suffix}`;
};

export const formatSetTime = (set) =>
  `${formatClock(set.start)} – ${formatClock(set.end)}`;

// Every set an act plays, with its stage, in running order.
export const setsForAct = (actName) => {
  const key = slugify(actName);
  return STAGES.flatMap((stage) =>
    stage.sets
      .filter((set) => slugify(set.act) === key)
      .map((set) => ({ ...set, stage: stage.name, stageColor: stage.color }))
  ).sort((a, b) => toMinutes(a.start) - toMinutes(b.start));
};

// Date and minutes-after-midnight in Culver City for any instant.
export const festivalClock = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: FESTIVAL.timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(now)
    .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
  };
};

// "before" | "live" | "after", judged against festival hours in Culver City.
export const festivalPhase = (now = new Date()) => {
  if (now < new Date(FESTIVAL.startsAt)) return "before";
  if (now < new Date(FESTIVAL.endsAt)) return "live";
  return "after";
};

// On festival day: what is playing now and next on each stage.
export const liveLineup = (now = new Date()) => {
  const { date, minutes } = festivalClock(now);
  if (date !== FESTIVAL.date) return [];
  return STAGES.map((stage) => {
    const current = stage.sets.find(
      (set) => toMinutes(set.start) <= minutes && minutes < toMinutes(set.end)
    );
    const next = stage.sets.find((set) => toMinutes(set.start) > minutes);
    return { stage, current: current || null, next: next || null };
  }).filter((entry) => entry.current || entry.next);
};

// On festival day: the roaming dance performance, if it is happening now.
export const roamingNow = (now = new Date()) => {
  const { date, minutes } = festivalClock(now);
  if (date !== FESTIVAL.date) return null;
  return (
    SPECIAL_PERFORMANCE.sets.find(
      (set) => toMinutes(set.start) <= minutes && minutes < toMinutes(set.end)
    ) || null
  );
};

export const isSetLive = (set, now = new Date()) => {
  const { date, minutes } = festivalClock(now);
  return (
    date === FESTIVAL.date &&
    toMinutes(set.start) <= minutes &&
    minutes < toMinutes(set.end)
  );
};

export const googleCalendarUrl = () => {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: FESTIVAL.name,
    // 11am–6pm Pacific (PDT), in UTC.
    dates: "20261010T180000Z/20261011T010000Z",
    ctz: FESTIVAL.timeZone,
    location: `${FESTIVAL.street}, ${FESTIVAL.district}, ${FESTIVAL.city}`,
    details: `FREE festival, 11am–6pm: artists, live music on three stages, food trucks, beer gardens, and kids' activities. Lineup: ${FESTIVAL.siteUrl}/entertainment`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};
